import React, { useEffect } from 'react';
import { App as AntApp, Form, Input, Button, Typography } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  KeyOutlined,
  SafetyOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RegisterRequest } from '@/types/account';
import { accountService } from '@/services/accountService';
import { usernameRules } from '@/utils/username';
import styles from './Register.module.css';

const { Title } = Typography;

/**
 * 注册页（邀请码实名制）：
 * - 邀请码由老师按名单发放，一人一码；「真实姓名」必须与邀请码登记的姓名一致，
 *   否则后端返回 400 且不会核销邀请码；
 * - 姓名/学校注册后由系统按邀请码预设写入并锁定，学生不可自行修改；
 * - 支持邀请链接 ?code=XXXX 预填邀请码（老师可把链接直接发给学生）。
 */
const Register: React.FC = () => {
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();

  // 邀请链接预填（/register?code=XXXX）
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      form.setFieldsValue({ invitation_code: code.trim().toUpperCase() });
    }
  }, [searchParams, form]);

  const onFinish = async (values: RegisterRequest) => {
    try {
      // 邀请码统一大写（后端码为 A-Z0-9）
      await accountService.register({
        ...values,
        invitation_code: values.invitation_code.trim().toUpperCase(),
        real_name: values.real_name.trim(),
      });
      message.success('注册成功！请使用刚设置的账号登录');
      navigate('/login');
    } catch (error: any) {
      const resp = error.response?.data;
      // 后端字段级错误（data 为 {field: [msg]}）优先回填到表单
      if (resp?.data && typeof resp.data === 'object' && !Array.isArray(resp.data)) {
        Object.entries(resp.data).forEach(([key, messages]) => {
          form.setFields([{ name: key, errors: messages as string[] }]);
        });
        return;
      }
      // 其余情况展示后端业务提示（如「姓名与该邀请码不符…」）
      message.error(resp?.message || '注册失败，请稍后重试');
    }
  };

  return (
    <div className={`${styles.registerContainer} registerContainer`}>
      <div className={styles.decorationBackground}>
        <div className={styles.circle1}></div>
        <div className={styles.circle2}></div>
        <div className={styles.circle3}></div>
        <div className={styles.square1}></div>
        <div className={styles.square2}></div>
      </div>

      <div className={styles.formWrapper}>
        <Form
          form={form}
          name="register"
          onFinish={onFinish}
          layout="vertical"
          className={styles.registerForm}
        >
          <Title level={2} className={styles.formTitle}>加入 Jupiter OJ</Title>
          <p className={styles.formSubtitle}>使用老师发放的邀请码创建账户</p>

          <Form.Item
            name="invitation_code"
            label="邀请码"
            rules={[{ required: true, message: '请输入邀请码' }]}
            extra="邀请码由老师按名单发放，一人一码"
          >
            <Input
              prefix={<KeyOutlined className={styles.inputIcon} />}
              placeholder="请输入邀请码"
              size="large"
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            name="real_name"
            label="真实姓名"
            rules={[
              { required: true, message: '请输入真实姓名' },
              { max: 50, message: '真实姓名不能超过50个字符' },
            ]}
            extra="需与老师名单上的姓名一致，注册后不可自行修改"
          >
            <Input
              prefix={<IdcardOutlined className={styles.inputIcon} />}
              placeholder="请输入真实姓名"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="username"
            label="用户名"
            rules={usernameRules()}
          >
            <Input
              prefix={<UserOutlined className={styles.inputIcon} />}
              placeholder="登录用的用户名"
              size="large"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6个字符' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className={styles.inputIcon} />}
              placeholder="密码"
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="password_confirm"
            label="确认密码"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<SafetyOutlined className={styles.inputIcon} />}
              placeholder="确认密码"
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large" className={styles.submitButton}>
              注册
            </Button>
          </Form.Item>

          <div className={styles.formFooter}>
            <Button type="link" onClick={() => navigate('/login')}>
              已有账号？立即登录
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default Register;
